/**
 * Admin Driver Management Page
 * Full CRUD: View, Add, Approve, Reject, Suspend, Delete drivers
 * + Send Invite Email feature
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Building2, Link2, 
  ArrowLeft, 
  Truck,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  FileText,
  User,
  Car,
  Plus,
  Trash2,
  Ban,
  Play,
  X,
  Mail,
  Send
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  suspended: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const VEHICLE_ICONS = {
  bicycle: '🚲',
  motorcycle: '🏍️',
  car: '🚗',
  van: '🚐',
  truck: '🚚',
};

const AdminDriverManagement = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [inviteDriver, setInviteDriver] = useState(null);
  
  // Add driver form
  const [newDriver, setNewDriver] = useState({
    name: '',
    user_email: '',
    vehicle_type: 'motorcycle',
    license_plate: '',
    ppv_id: '',
    phone: '',
    send_credentials_email: true
  });

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchDrivers();
  }, [isAdmin, navigate]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/drivers/');
      setDrivers(response.data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  

  const fetchBankDetails = async (driverId) => {
    setBankLoading(true);
    try {
      const response = await api.get(`/admin/drivers/${driverId}/bank-details`);
      setBankDetails(response.data);
      setShowBankModal(true);
    } catch (error) {
      console.error("Error fetching bank details:", error);
      toast.error("Failed to load bank details");
    } finally {
      setBankLoading(false);
    }
  };

  const handleApprove = async (driverId) => {
    setActionLoading(driverId);
    try {
      await api.post(`/admin/drivers/${driverId}/approve`);
      toast.success('Driver approved successfully');
      fetchDrivers();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve driver');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (driverId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    setActionLoading(driverId);
    try {
      await api.post(`/admin/drivers/${driverId}/reject`, null, { params: { reason } });
      toast.success('Driver rejected');
      fetchDrivers();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject driver');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (driverId) => {
    if (!window.confirm('Suspend this driver?')) return;
    
    setActionLoading(driverId);
    try {
      await api.post(`/admin/drivers/${driverId}/suspend`);
      toast.success('Driver suspended');
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend driver');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (driverId) => {
    setActionLoading(driverId);
    try {
      await api.post(`/admin/drivers/${driverId}/reactivate`);
      toast.success('Driver reactivated');
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate driver');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('Permanently delete this driver? This action cannot be undone.')) return;
    
    setActionLoading(driverId);
    try {
      await api.delete(`/admin/drivers/${driverId}`);
      toast.success('Driver deleted');
      fetchDrivers();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete driver');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddDriver = async () => {
    if (!newDriver.user_email || !newDriver.license_plate) {
      toast.error('Email and license plate are required');
      return;
    }
    
    setActionLoading('add');
    try {
      const response = await api.post('/admin/drivers/create', newDriver);
      toast.success('Driver added successfully');
      setShowAddModal(false);
      
      // Show option to send invite email
      const driverData = response.data;
      if (driverData?.driver_id) {
        // Find the newly created driver and offer to send invite
        await fetchDrivers();
        const newDriverRecord = drivers.find(d => d.id === driverData.driver_id) || {
          id: driverData.driver_id,
          user_email: newDriver.user_email,
          user_name: 'New Driver'
        };
        setInviteDriver(newDriverRecord);
        setShowInviteModal(true);
      }
      
      setNewDriver({ name: '', user_email: '', vehicle_type: 'motorcycle', license_plate: '', ppv_id: '',
    phone: '',
    send_credentials_email: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add driver');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendInviteEmail = async (driver, includePassword = true) => {
    setActionLoading(`invite-${driver.id}`);
    try {
      const response = await api.post(`/admin/invites/send-driver-invite/${driver.id}`, null, {
        params: { include_temp_password: includePassword }
      });
      
      if (response.data.success) {
        toast.success(`Invite email sent to ${response.data.email_sent_to}`);
        setShowInviteModal(false);
        setInviteDriver(null);
      } else {
        toast.error(response.data.message || 'Failed to send invite');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invite email');
    } finally {
      setActionLoading(null);
    }
  };

  const viewDriverDetails = (driver) => {
    setSelectedDriver(driver);
    setShowDetailModal(true);
  };

  const openInviteModal = (driver) => {
    setInviteDriver(driver);
    setShowInviteModal(true);
  };

  // Filter drivers
  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      driver.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      driver.license_plate?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || driver.verification_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: drivers.length,
    pending: drivers.filter(d => d.verification_status === 'pending').length,
    approved: drivers.filter(d => d.verification_status === 'approved').length,
    suspended: drivers.filter(d => d.verification_status === 'suspended').length,
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-amber-500" />
              <span className="text-xl font-bold text-white tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-blue-400 bg-blue-500/10 px-2 py-1 rounded border border-blue-500/20">
                DRIVER MANAGEMENT
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchDrivers}
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              disabled={loading}
              data-testid="refresh-drivers-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Truck className="h-5 w-5 text-slate-400" />
              <span className="text-slate-400 text-sm">Total Drivers</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-yellow-400" />
              <span className="text-slate-400 text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-5 w-5 text-teal-400" />
              <span className="text-slate-400 text-sm">Approved</span>
            </div>
            <p className="text-2xl font-bold text-teal-400">{stats.approved}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Ban className="h-5 w-5 text-orange-400" />
              <span className="text-slate-400 text-sm">Suspended</span>
            </div>
            <p className="text-2xl font-bold text-orange-400">{stats.suspended}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or license plate..."
              className="bg-slate-900 border-slate-700 pl-10 text-white"
              data-testid="driver-search-input"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'suspended', 'rejected'].map((status) => (
              <Button
                key={status}
                onClick={() => setStatusFilter(status)}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                className={statusFilter === status 
                  ? 'bg-amber-500 text-slate-950' 
                  : 'border-slate-700 text-slate-300'
                }
                data-testid={`filter-${status}-btn`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Drivers Table */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden" data-testid="drivers-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/50">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Driver</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Vehicle</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Contact</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Status</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Stats</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center">
                      <RefreshCw className="h-6 w-6 text-amber-500 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : filteredDrivers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      No drivers found
                    </td>
                  </tr>
                ) : (
                  filteredDrivers.map((driver) => (
                    <tr key={driver.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors" data-testid={`driver-row-${driver.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{driver.user_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500 font-mono">{driver.id?.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{VEHICLE_ICONS[driver.vehicle_type] || '🚗'}</span>
                          <div>
                            <p className="text-white capitalize">{driver.vehicle_type}</p>
                            <p className="text-xs text-slate-400 font-mono">{driver.license_plate}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-slate-300 text-sm">{driver.user_email || '-'}</p>
                        <p className="text-slate-400 text-xs">{driver.user_phone || '-'}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono border ${STATUS_COLORS[driver.verification_status] || STATUS_COLORS.pending}`}>
                          {driver.verification_status || 'pending'}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        <p className="text-slate-300">{driver.total_deliveries || 0} deliveries</p>
                        <p className="text-slate-400">⭐ {driver.rating_average?.toFixed(1) || '0.0'}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button onClick={() => viewDriverDetails(driver)} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="View Details" data-testid={`view-driver-${driver.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button onClick={() => openInviteModal(driver)} variant="ghost" size="sm" className="text-blue-400 hover:bg-blue-500/10" title="Send Invite Email" data-testid={`invite-driver-${driver.id}`}>
                            <Mail className="h-4 w-4" />
                          </Button>
                          {driver.verification_status === 'pending' && (
                            <>
                              <Button onClick={() => handleApprove(driver.id)} variant="ghost" size="sm" className="text-teal-400 hover:bg-teal-500/10" title="Approve" disabled={actionLoading === driver.id} data-testid={`approve-driver-${driver.id}`}>
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button onClick={() => handleReject(driver.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" title="Reject" disabled={actionLoading === driver.id} data-testid={`reject-driver-${driver.id}`}>
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {driver.verification_status === 'approved' && (
                            <Button onClick={() => handleSuspend(driver.id)} variant="ghost" size="sm" className="text-orange-400 hover:bg-orange-500/10" title="Suspend" disabled={actionLoading === driver.id} data-testid={`suspend-driver-${driver.id}`}>
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          {driver.verification_status === 'suspended' && (
                            <Button onClick={() => handleReactivate(driver.id)} variant="ghost" size="sm" className="text-teal-400 hover:bg-teal-500/10" title="Reactivate" disabled={actionLoading === driver.id} data-testid={`reactivate-driver-${driver.id}`}>
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button onClick={() => handleDelete(driver.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" title="Delete" disabled={actionLoading === driver.id} data-testid={`delete-driver-${driver.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add Driver Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="add-driver-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Truck className="h-5 w-5 text-teal-500" />
                Add New Driver
              </h2>
              <Button onClick={() => setShowAddModal(false)} variant="ghost" size="sm" className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
              <div>
                <Label className="text-slate-300">Driver Name *</Label>
                <Input
                  value={newDriver.name}
                  onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="new-driver-name"
                />
              </div>
                <Label className="text-slate-300">User Email *</Label>
                <Input
                  value={newDriver.user_email}
                  onChange={(e) => setNewDriver({ ...newDriver, user_email: e.target.value })}
                  placeholder="driver@example.com"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="new-driver-email"
                />
              </div>
              <div>
                <Label className="text-slate-300">Vehicle Type</Label>
                <select
                  value={newDriver.vehicle_type}
                  onChange={(e) => setNewDriver({ ...newDriver, vehicle_type: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-2"
                  data-testid="new-driver-vehicle"
                >
                  <option value="bicycle">🚲 Bicycle</option>
                  <option value="motorcycle">🏍️ Motorcycle</option>
                  <option value="car">🚗 Car</option>
                  <option value="van">🚐 Van</option>
                  <option value="truck">🚚 Truck</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300">License Plate *</Label>
                <Input
                  value={newDriver.license_plate}
                  onChange={(e) => setNewDriver({ ...newDriver, license_plate: e.target.value })}
                  placeholder="JM-1234"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="new-driver-license"
                />
              </div>
              <div>
                <Label className="text-slate-300">PPV ID (Optional)</Label>
                <Input
                  value={newDriver.ppv_id}
                  onChange={(e) => setNewDriver({ ...newDriver, ppv_id: e.target.value })}
                  placeholder="PPV-12345"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="new-driver-ppv"
                />
              </div>
              <div>
                <Label className="text-slate-300">Telephone (Optional)</Label>
                <Input
                  value={newDriver.phone}
                  onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                  placeholder="+1 876 123 4567"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="new-driver-phone"
                />
              </div>
              <div className="flex items-center gap-3 pt-2 border-t border-slate-700">
                <input
                  type="checkbox"
                  id="send-credentials"
                  checked={newDriver.send_credentials_email}
                  onChange={(e) => setNewDriver({ ...newDriver, send_credentials_email: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-teal-500 focus:ring-teal-500"
                  data-testid="send-credentials-checkbox"
                />
                <label htmlFor="send-credentials" className="text-slate-300 text-sm">
                  Send login credentials via email
                </label>
                <Input
                  value={newDriver.ppv_id}
                  onChange={(e) => setNewDriver({ ...newDriver, ppv_id: e.target.value })}
                  placeholder="PPV-12345"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="new-driver-ppv"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowAddModal(false)} variant="outline" className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleAddDriver} className="flex-1 bg-teal-500 text-white hover:bg-teal-400" disabled={actionLoading === 'add'} data-testid="submit-new-driver">
                {actionLoading === 'add' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Driver
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Invite Email Modal */}
      {showInviteModal && inviteDriver && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="invite-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-500" />
                Send Invite Email
              </h2>
              <Button onClick={() => { setShowInviteModal(false); setInviteDriver(null); }} variant="ghost" size="sm" className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-slate-400 text-sm mb-1">Sending invite to:</p>
              <p className="text-white font-medium">{inviteDriver.user_name || 'Driver'}</p>
              <p className="text-slate-300 text-sm">{inviteDriver.user_email}</p>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
              <p className="text-blue-400 text-sm">
                <strong>Note:</strong> This will generate a temporary password and send it to the driver via email. They will be prompted to change their password on first login.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={() => { setShowInviteModal(false); setInviteDriver(null); }} variant="outline" className="flex-1 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={() => handleSendInviteEmail(inviteDriver, true)} 
                className="flex-1 bg-blue-500 text-white hover:bg-blue-400" 
                disabled={actionLoading === `invite-${inviteDriver.id}`}
                data-testid="send-invite-btn"
              >
                {actionLoading === `invite-${inviteDriver.id}` ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Invite
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedDriver && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="driver-detail-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Truck className="h-5 w-5 text-amber-500" />
                Driver Details
              </h2>
              <Button onClick={() => setShowDetailModal(false)} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Name</p>
                  <p className="text-white font-medium">{selectedDriver.user_name || 'Unknown'}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-mono border ${STATUS_COLORS[selectedDriver.verification_status] || STATUS_COLORS.pending}`}>
                    {selectedDriver.verification_status || 'pending'}
                  </span>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Vehicle</p>
                  <p className="text-white">{VEHICLE_ICONS[selectedDriver.vehicle_type]} {selectedDriver.vehicle_type} - {selectedDriver.license_plate}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">PPV ID</p>
                  <p className="text-white font-mono">{selectedDriver.ppv_id || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{selectedDriver.total_deliveries || 0}</p>
                  <p className="text-slate-400 text-sm">Deliveries</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-teal-400">${(selectedDriver.earnings_balance || 0).toFixed(2)}</p>
                  <p className="text-slate-400 text-sm">Earnings</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-amber-400">{selectedDriver.rating_average?.toFixed(1) || '0.0'}</p>
                  <p className="text-slate-400 text-sm">Rating</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-800 flex gap-3 flex-wrap">
              <Button 
                onClick={() => openInviteModal(selectedDriver)} 
                className="bg-blue-500 text-white hover:bg-blue-400"
                data-testid="detail-send-invite-btn"
              >
                <Mail className="h-4 w-4 mr-2" /> Send Invite Email
              </Button>
              {selectedDriver.verification_status === 'pending' && (
                <>
                  <Button onClick={() => handleApprove(selectedDriver.id)} className="bg-teal-500 text-white" disabled={actionLoading === selectedDriver.id}>
                    <CheckCircle className="h-4 w-4 mr-2" /> Approve
                  </Button>
                  <Button onClick={() => handleReject(selectedDriver.id)} variant="outline" className="border-red-500/30 text-red-400" disabled={actionLoading === selectedDriver.id}>
                    <XCircle className="h-4 w-4 mr-2" /> Reject
                  </Button>
                </>
              )}
              {selectedDriver.verification_status === 'approved' && (
                <Button onClick={() => handleSuspend(selectedDriver.id)} variant="outline" className="border-orange-500/30 text-orange-400" disabled={actionLoading === selectedDriver.id}>
                  <Ban className="h-4 w-4 mr-2" /> Suspend
                </Button>
              )}
              {selectedDriver.verification_status === 'suspended' && (
                <Button onClick={() => handleReactivate(selectedDriver.id)} className="bg-teal-500 text-white" disabled={actionLoading === selectedDriver.id}>
                  <Play className="h-4 w-4 mr-2" /> Reactivate
                </Button>
              )}
              <Button onClick={() => handleDelete(selectedDriver.id)} variant="outline" className="border-red-500/30 text-red-400 ml-auto" disabled={actionLoading === selectedDriver.id}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Details Modal */}
      {showBankModal && bankDetails && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-emerald-500/20 text-emerald-400">
                  <Building2 className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-white">Bank Details</h2>
              </div>
              <button onClick={() => setShowBankModal(false)} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm mb-1">Driver Name</p>
                <p className="text-white font-medium">{bankDetails.driver_name || "N/A"}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm mb-1">Email</p>
                <p className="text-white font-mono text-sm">{bankDetails.driver_email || "N/A"}</p>
              </div>
              
              {bankDetails.has_bank_details ? (
                <>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <p className="text-emerald-400 text-sm font-medium mb-3">Bank Information</p>
                    <div className="space-y-3">
                      <div>
                        <p className="text-slate-400 text-xs">Bank Name</p>
                        <p className="text-white">{bankDetails.bank_details?.bank_name || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Account Number</p>
                        <p className="text-white font-mono">{bankDetails.bank_details?.account_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Account Holder</p>
                        <p className="text-white">{bankDetails.bank_details?.account_holder_name || "N/A"}</p>
                      </div>
                      {bankDetails.bank_details?.branch && (
                        <div>
                          <p className="text-slate-400 text-xs">Branch</p>
                          <p className="text-white">{bankDetails.bank_details.branch}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
                  <p className="text-amber-400">No bank details saved</p>
                  <p className="text-slate-400 text-sm mt-1">Driver has not added bank information yet</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-800">
              <Button onClick={() => setShowBankModal(false)} className="w-full bg-slate-700 hover:bg-slate-600 text-white">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDriverManagement;
